import { Icon } from '@stratumn/atomic';

import {
  Bold,
  Heading1,
  Italic,
  LinkIcon,
  List,
  Redo2,
  Underline,
  Undo2
} from 'lucide-react';
import { useSlate } from 'slate-react';

import { TooltipProvider } from '@/shadcn/tooltip';

import { BLOCK_TYPES } from '@/components/ui/richEditor/constants.richEditor';
import {
  deleteColumn,
  deleteRow,
  deleteTable,
  insertColumn,
  insertRow,
  insertTable,
  isTableActive
} from '@/components/ui/richEditor/tables/table.utils.richEditor';
import { ToolbarButton } from '@/components/ui/richEditor/toolbar/button.toolbar.richEditor';
import {
  isBlockActive,
  isLinkActive,
  isMarkActive,
  isSubjectActive,
  toggleBlock,
  toggleLink,
  toggleMark
} from '@/components/ui/richEditor/utils';

type ToolbarProps = {
  disabled?: boolean;
};

export const Toolbar = ({ disabled = false }: ToolbarProps) => {
  const editor = useSlate();
  const isDisabled = disabled || isSubjectActive(editor);
  const isInTable = isTableActive(editor);

  return (
    <TooltipProvider>
      <div className="mb-2 flex flex-wrap justify-start gap-4">
        <div className="flex flex-wrap gap-1">
          <ToolbarButton
            disabled={isDisabled}
            isActive={isMarkActive(editor, 'bold')}
            onClick={() => toggleMark(editor, 'bold')}
            tooltipContent="Bold"
          >
            <Bold />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled}
            isActive={isMarkActive(editor, 'italic')}
            onClick={() => toggleMark(editor, 'italic')}
            tooltipContent="Italic"
          >
            <Italic />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled}
            isActive={isMarkActive(editor, 'underline')}
            onClick={() => toggleMark(editor, 'underline')}
            tooltipContent="Underline"
          >
            <Underline />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled || isInTable}
            isActive={isBlockActive(editor, BLOCK_TYPES.HEADING1, 'type')}
            onClick={() => toggleBlock(editor, BLOCK_TYPES.HEADING1)}
            tooltipContent="Heading 1"
          >
            <Heading1 />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled || isInTable}
            isActive={isBlockActive(editor, BLOCK_TYPES.UNORDERED_LIST, 'type')}
            onClick={() => toggleBlock(editor, BLOCK_TYPES.UNORDERED_LIST)}
            tooltipContent="Unordered list"
          >
            <List />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled}
            isActive={isLinkActive(editor)}
            onClick={() => toggleLink(editor)}
            tooltipContent="Add a link"
          >
            <LinkIcon />
          </ToolbarButton>

          <ToolbarButton
            disabled={isDisabled || isInTable}
            onClick={() => insertTable(editor)}
            tooltipContent="Insert a table"
          >
            <Icon name="TableInsert" size={16} />
          </ToolbarButton>
        </div>
        {isInTable && (
          <div className="flex flex-wrap gap-1">
            <ToolbarButton
              disabled={isDisabled}
              onClick={() => insertRow(editor)}
              tooltipContent="Insert a new row"
            >
              <Icon name="RowInsert" size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              onClick={() => deleteRow(editor)}
              tooltipContent="Delete current row"
            >
              <Icon name="RowDelete" size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              onClick={() => insertColumn(editor)}
              tooltipContent="Insert a new column"
            >
              <Icon name="ColumnInsert" size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              onClick={() => deleteColumn(editor)}
              tooltipContent="Delete current column"
            >
              <Icon name="ColumnDelete" size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              onClick={() => deleteTable(editor)}
              tooltipContent="Delete current table"
            >
              <Icon name="TableDelete" size={16} />
            </ToolbarButton>
          </div>
        )}
        <div className="ml-auto flex flex-wrap gap-1">
          <ToolbarButton
            disabled={isDisabled || editor.history.undos.length <= 1}
            onClick={editor.undo}
            tooltipContent="Undo last change"
          >
            <Undo2 />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled || editor.history.redos.length === 0}
            onClick={editor.redo}
            tooltipContent="Redo last change"
          >
            <Redo2 />
          </ToolbarButton>
        </div>
      </div>
    </TooltipProvider>
  );
};
